import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Minus } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: "easeOut", delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const TREATMENTS = [
  {
    slug: "studio-a-ritual",
    name: "The Studio A Ritual",
    duration: "90-minute personalised facial",
    description:
      "A personalised facial combining advanced skin technology, sculpting massage and deep relaxation.",
    bookLabel: "Book the Facial Ritual",
    groups: [
      { title: "On Arrival", items: ["Herbal tea", "Personal consultation", "Skin analysis"] },
      {
        title: "Your Treatment",
        items: [
          "Personalised facial treatment",
          "Hydrafacial",
          "LED light therapy",
          "Radiofrequency where suitable",
          "Lymphatic drainage",
          "Sculpting facial massage",
        ],
      },
      { title: "After Your Ritual", items: ["Personalised aftercare guidance", "Herbal tea"] },
    ],
  },
  {
    slug: "deep-body-ritual",
    name: "The Deep Body Ritual",
    duration: "90-minute full-body massage",
    description:
      "An intuitive full-body massage designed to release tension, support lymphatic flow and restore calm.",
    bookLabel: "Book the Body Ritual",
    groups: [
      { title: "On Arrival", items: ["Herbal tea", "Personal consultation"] },
      {
        title: "Your Treatment",
        items: [
          "Thai-inspired techniques",
          "European massage techniques",
          "Intuitive touch",
          "Lymphatic focus",
          "Scalp massage",
          "Facial massage",
        ],
      },
      { title: "After Your Ritual", items: ["Time to settle", "Herbal tea"] },
    ],
  },
  {
    slug: "breathwork",
    name: "One-to-One Breathwork",
    duration: "60-minute private session",
    description:
      "A private breathwork and meditation session designed to support nervous-system regulation and inner calm.",
    bookLabel: "Book Breathwork",
    groups: [
      { title: "On Arrival", items: ["Conversation", "Herbal tea"] },
      {
        title: "Your Practice",
        items: ["Guided breathwork", "Meditation", "Music", "Nervous-system regulation practices"],
      },
      { title: "After Your Session", items: ["Time for questions", "Simple breathing tools to take home"] },
    ],
  },
];

const TreatmentCard = ({ t }) => (
  <article data-testid={`treatment-card-${t.slug}`} className="card-edge bg-[#F7F6F2] p-6 sm:p-10 lg:p-12">
    <p className="label-eyebrow">{t.duration}</p>
    <h2 className="serif mt-4 text-[28px] sm:text-[36px] lg:text-[40px] leading-[1.15] text-[#2F2C28]">
      {t.name}
    </h2>
    <p className="mt-4 text-[15px] sm:text-[16px] leading-[1.8] text-[#2F2C28] max-w-xl">
      {t.description}
    </p>
    <div className="mt-7">
      <Link to="/book" data-testid={`book-${t.slug}`} className="btn-primary inline-flex">
        {t.bookLabel} <ArrowRight size={14} strokeWidth={1.6} />
      </Link>
    </div>

    <AccordionPrimitive.Item value={t.slug} className="mt-8 border-t border-[#D5CDBF]">
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          data-testid={`accordion-trigger-${t.slug}`}
          className="group flex flex-1 items-center justify-between gap-4 min-h-[52px] py-4 text-left text-[12px] uppercase tracking-[0.22em] font-medium text-[#2F2C28] transition-colors hover:text-[#A96A53] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#A96A53]"
        >
          <span>What&rsquo;s Included</span>
          <span aria-hidden="true" className="shrink-0 grid place-items-center h-8 w-8">
            <Plus size={16} strokeWidth={1.6} className="group-data-[state=open]:hidden" />
            <Minus size={16} strokeWidth={1.6} className="hidden group-data-[state=open]:block" />
          </span>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content
        data-testid={`accordion-content-${t.slug}`}
        className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      >
        <div className="pb-6 pt-1 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6">
          {t.groups.map((g) => (
            <div key={g.title}>
              <p className="label-eyebrow">{g.title}</p>
              <ul className="mt-4 space-y-2.5">
                {g.items.map((item) => (
                  <li key={item} className="text-[14px] leading-[1.7] text-[#2F2C28]">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  </article>
);

export default function Treatments() {
  return (
    <div data-testid="treatments-page">
      <section className="pt-24 lg:pt-36 pb-12 lg:pb-16">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="label-eyebrow">Treatments</div>
            <h1 className="serif mt-6 text-[30px] sm:text-[42px] lg:text-[48px] leading-[1.2] tracking-tight text-[#2F2C28]">
              Personalised treatments for <span className="italic">skin, body and breath</span>, created to meet you where you are.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10">
          <AccordionPrimitive.Root type="single" collapsible className="space-y-8 lg:space-y-10">
            {TREATMENTS.map((t, i) => (
              <Reveal key={t.slug} delay={0.05 * i}>
                <TreatmentCard t={t} />
              </Reveal>
            ))}

            <Reveal delay={0.2}>
              <article data-testid="treatment-card-skin-needling" className="card-edge bg-[#EAE7E0] p-6 sm:p-10 lg:p-12">
                <p className="label-eyebrow text-[#A96A53]">Coming Soon</p>
                <h2 data-testid="skin-needling-coming-soon" className="serif mt-4 text-[28px] sm:text-[36px] lg:text-[40px] leading-[1.15] text-[#2F2C28]">
                  Skin Needling
                </h2>
                <p className="mt-4 text-[15px] sm:text-[16px] leading-[1.8] text-[#2F2C28]">Coming Soon</p>
              </article>
            </Reveal>
          </AccordionPrimitive.Root>
        </div>
      </section>
    </div>
  );
}
